import type { UnfinishedControllerCounts, UnfinishedTaskMeta, UnfinishedTasksByApp } from '@wix/thunderbolt-symbols'
import { WixCodeAppDefId } from './core/constants'

// Counts unfinished tasks per app and controller type. The parts are carried structurally from the
// registering call site because the flat task name cannot be split back reliably - the phase, the
// app definition id and the controller type can all contain underscores.
// Tasks registered without an app (runApplications, repeater lifecycles) are omitted; they stay
// visible in the flat task list.
export const APP_LEVEL_TASK = '(app)'

// Accumulated in Maps because app ids and controller types are unrestricted model strings: on plain
// objects a key like 'constructor' reads as an inherited value and '__proto__' writes the prototype,
// which would corrupt or throw while building the log for exactly those apps.
export function groupUnfinishedTasksByApp(metas: Array<UnfinishedTaskMeta | undefined>): UnfinishedTasksByApp {
	const grouped = new Map<string, Map<string, number>>()

	metas.forEach((meta) => {
		if (!meta?.appDefinitionId) {
			return
		}
		let countsByControllerType = grouped.get(meta.appDefinitionId)
		if (!countsByControllerType) {
			countsByControllerType = new Map()
			grouped.set(meta.appDefinitionId, countsByControllerType)
		}
		const controllerType = meta.controllerType || APP_LEVEL_TASK
		countsByControllerType.set(controllerType, (countsByControllerType.get(controllerType) || 0) + 1)
	})

	return Object.fromEntries(
		[...grouped].map(([appDefinitionId, countsByControllerType]) => [
			appDefinitionId,
			Object.fromEntries(countsByControllerType),
		])
	)
}

// Controller lifecycles that belong to no single app - repeater controllers above all. They are
// counted separately rather than folded into the per-app view, which would need an app id they do
// not have. Without this a page can log hundreds of stuck repeater controllers and show none of
// them, since the per-app view only sees app-scoped work.
export function groupUnfinishedControllers(metas: Array<UnfinishedTaskMeta | undefined>): UnfinishedControllerCounts {
	const counts = new Map<string, number>()

	metas.forEach((meta) => {
		if (!meta?.taskKind || meta.appDefinitionId) {
			return
		}
		counts.set(meta.taskKind, (counts.get(meta.taskKind) || 0) + 1)
	})

	return Object.fromEntries(counts)
}

/**
 * Everything still in flight when the platform failed. Sections are omitted when empty, so a plain
 * Velo timeout logs as just { wixCode: 1 }.
 */
export type UnfinishedTaskCounts = {
	apps?: UnfinishedTasksByApp
	controllers?: UnfinishedControllerCounts
	wixCode?: number
}

// One summary of everything still in flight: app-scoped work, controller lifecycles that belong to
// no app, and Velo. Velo is the site's own code rather than an installed app and appears in roughly
// half of all platform SSR failures, always under the same app definition id, so it is reported as
// a plain count instead of spending 56 characters on an id that never varies. Keyed on
// WixCodeAppDefId, so an id that is not Velo's stays under apps rather than being mislabelled.
// Empty sections are omitted.
export function countUnfinishedTasks(
	groupedByApp: UnfinishedTasksByApp,
	controllers: UnfinishedControllerCounts
): UnfinishedTaskCounts {
	const { [WixCodeAppDefId]: wixCodeCountsByControllerType, ...apps } = groupedByApp
	const wixCode = Object.values(wixCodeCountsByControllerType ?? {}).reduce((total, n) => total + n, 0)

	return {
		...(Object.keys(apps).length ? { apps } : {}),
		...(Object.keys(controllers).length ? { controllers } : {}),
		...(wixCode ? { wixCode } : {}),
	}
}
